import { supabase } from '@/shared/lib/supabase';
import { logger } from '@/shared/lib/logger';
import { AppError } from '@/shared/lib/errors';
import {
  acceptResponseSchema,
  inviteRequestSchema,
  inviteResponseSchema,
  type AcceptResponse,
  type InviteRequest,
  type InviteResponse,
} from '@shared/schemas/groups';

interface EdgeFunctionResponse<T> {
  ok: boolean;
  data?: T;
  error?: { code: string; message: string; context?: Record<string, unknown> };
}

/**
 * Saca el error de la función del cuerpo de una respuesta no-2xx.
 *
 * `FunctionsHttpError` lleva la `Response` entera en `context`; el resto de
 * errores de `functions.invoke` —red caída, respuesta que no es JSON— no la
 * llevan, y para esos se devuelve `null` y manda el camino de siempre. El
 * `catch` está porque el cuerpo puede haberse consumido ya o no ser JSON: si no
 * se puede leer, tampoco pasa nada, se cae al genérico.
 */
async function leerErrorHttp(
  error: unknown
): Promise<{ code: string; message: string; status: number; context: Record<string, unknown> } | null> {
  const res = (error as { context?: unknown }).context;
  if (!(res instanceof Response)) return null;
  try {
    const cuerpo = (await res.clone().json()) as EdgeFunctionResponse<unknown>;
    if (!cuerpo?.error?.code) return null;
    return {
      code: cuerpo.error.code,
      message: cuerpo.error.message,
      status: res.status,
      context: cuerpo.error.context ?? {},
    };
  } catch {
    return null;
  }
}

async function invokeFunction<TResp>(
  name: string,
  body: unknown
): Promise<TResp> {
  const { data, error } = await supabase.functions.invoke<EdgeFunctionResponse<TResp>>(name, {
    body: body as Record<string, unknown>,
  });

  if (error) {
    // Una respuesta no-2xx llega aquí, no por `data`: `functions.invoke` la
    // convierte en `FunctionsHttpError` y deja `data` a null. Sin leer el
    // cuerpo, un `403 FORBIDDEN`, un `409 ALREADY_ACCEPTED` y un
    // `429 RATE_LIMITED` eran los tres el mismo `EDGE_TRANSPORT` con un mensaje
    // genérico, y la pantalla no tenía con qué distinguirlos. El código de la
    // función viene en el cuerpo, que sigue siendo JSON: se lee.
    const cuerpo = await leerErrorHttp(error);
    if (cuerpo) {
      logger.error('Edge function error', { name, code: cuerpo.code, status: cuerpo.status });
      throw new AppError(cuerpo.code, cuerpo.message, cuerpo.status, cuerpo.context);
    }
    logger.error('Edge function transport error', { name, message: error.message });
    throw new AppError('EDGE_TRANSPORT', error.message, 500);
  }
  if (!data) {
    throw new AppError('EDGE_EMPTY', 'Empty response', 500);
  }
  if (!data.ok) {
    const err = data.error ?? { code: 'UNKNOWN', message: 'Unknown error' };
    throw new AppError(err.code, err.message, 400, err.context ?? {});
  }
  if (data.data === undefined) {
    throw new AppError('EDGE_MALFORMED', 'Missing data in response', 500);
  }
  return data.data;
}

// ==================== Invite ====================

export async function inviteToGroup(input: InviteRequest): Promise<InviteResponse> {
  const parsed = inviteRequestSchema.parse(input);
  const raw = await invokeFunction<InviteResponse>('invite-to-group', parsed);
  return inviteResponseSchema.parse(raw);
}

// ==================== Accept ====================

export async function acceptInvitation(token: string): Promise<AcceptResponse> {
  const raw = await invokeFunction<AcceptResponse>('accept-invitation', { token });
  return acceptResponseSchema.parse(raw);
}

// ==================== List pending / resend / revoke ====================

export interface PendingInvitation {
  id: string;
  group_id: string;
  email: string;
  expires_at: string;
  created_at: string;
  invited_by: string;
  ghost_participant_id: string | null;
  ghost_display_name?: string | null;
}

export async function fetchPendingInvitations(groupId: string): Promise<PendingInvitation[]> {
  const { data, error } = await supabase
    .from('group_invitations')
    .select(`
      id, group_id, email, expires_at, created_at, invited_by, ghost_participant_id,
      ghost:participants!group_invitations_ghost_participant_id_fkey(display_name)
    `)
    .eq('group_id', groupId)
    .is('accepted_at', null)
    .gt('expires_at', new Date().toISOString())
    .order('created_at', { ascending: false });

  if (error) {
    logger.error('fetchPendingInvitations failed', { code: error.code, message: error.message });
    throw new AppError(error.code ?? 'DB_ERROR', error.message, 500);
  }

  return (data ?? []).map((row) => {
    const ghost = (row as unknown as { ghost: { display_name: string } | null }).ghost;
    return {
      id: row.id,
      group_id: row.group_id,
      email: row.email,
      expires_at: row.expires_at,
      created_at: row.created_at,
      invited_by: row.invited_by,
      ghost_participant_id: row.ghost_participant_id,
      ghost_display_name: ghost?.display_name ?? null,
    };
  });
}

export interface ResendResult {
  invitation_id: string;
  expires_at: string;
  email_sent: boolean;
  /**
   * `true` cuando por esta invitación no había salido ningún correo todavía —el
   * caso de darle correo a un fantasma, donde la RPC abre la invitación y el
   * envío es esta llamada—. Sirve para contarlo como «enviada» y no como
   * «reenviada», que es lo que el propio correo dice desde `20260809200000`.
   *
   * Opcional porque la v9 desplegada no lo devolvía: un cliente viejo contra la
   * v10 lo ve, uno nuevo contra una v9 que siguiera viva no.
   */
  first_time?: boolean;
}

export async function resendInvitation(invitationId: string): Promise<ResendResult> {
  return invokeFunction<ResendResult>('resend-invitation', { invitation_id: invitationId });
}

export async function revokeInvitation(invitationId: string): Promise<void> {
  const { error } = await supabase
    .from('group_invitations')
    .delete()
    .eq('id', invitationId);
  if (error) {
    logger.error('revokeInvitation failed', { code: error.code, message: error.message });
    throw new AppError(error.code ?? 'DB_ERROR', error.message, 500);
  }
}

// ==================== Search registered users (§3.1) ====================

export interface SearchedUser {
  id: string;
  display_name: string;
  avatar_url: string | null;
}

export async function searchRegisteredUsers(query: string, limit = 8): Promise<SearchedUser[]> {
  const trimmed = query.trim();
  if (trimmed.length < 2) return [];
  const { data, error } = await supabase.rpc('search_registered_users', {
    p_query: trimmed,
    p_limit: limit,
  });
  if (error) {
    logger.error('searchRegisteredUsers failed', { code: error.code });
    return [];
  }
  return (data ?? []) as SearchedUser[];
}

export interface AddUserResult {
  added: boolean;
  already_member?: boolean;
  rejoined?: boolean;
}

export async function addRegisteredUserToGroup(
  groupId: string,
  participantId: string
): Promise<AddUserResult> {
  const { data, error } = await supabase.rpc('add_registered_user_to_group', {
    p_group_id: groupId,
    p_participant_id: participantId,
  });
  if (error) {
    logger.error('addRegisteredUserToGroup failed', { code: error.code });
    throw new AppError(error.code ?? 'DB_ERROR', error.message, 500);
  }
  return data as unknown as AddUserResult;
}
